import React from 'react';
import styled from 'styled-components';

import { resolve } from 'library/helpers/path-resolver';
import Loading from 'components/loading';
import withData from 'util/hoc/with-data';
import AdminQuery from 'apps/admin/util/query';
import Query from 'apps/admin/util/query';
import ResetPassword from 'components/reset-password';
import {
  resetPasswordMutation,
  resetPasswordMutationParseQuery,
} from 'apps/admin/queries/users/resetPasswordMutation';
import Confirm from 'library/helpers/confirm';
import { jsonToInputParams } from 'library/util/graph';
import Notification, {
  Error as NotificationError,
  Success,
  NotificationRender,
} from 'library/components/notification';
import Toggle from 'library/components/form-elements/toggle';

const USER_TYPE_MAP = {
  user: 'User',
  'hiring manager': 'Hiring Manager',
  admin: 'Admin',
  'system admin': 'System Admin',
};

const StyledUserOverview = styled.div`
  display: flex;
  flex-direction: column;

  > label {
    display: flex;
    width: 100%;
    max-width: 500px;
    padding: 1em;
    border-radius: 4px;

    select {
      margin: 0;
    }

    span {
      display: flex;
      align-items: center;
      margin: 0;

      &:first-child {
        flex: 1;
      }
    }
  }
`;

class UserOverview extends React.Component {
  state = {
    reset_link: null,
    copyModalActive: false,
    saving: false,
  };

  renderMapUserRoles = ({ value, display_name }) => (
    <option value={value}> {display_name} </option>
  );

  toggleActive = async () => {
    await this.setState({ copyModalActive: !this.state.copyModalActive });
  };

  handleUserRole = async (e) => {
    const { match, reload_graph } = this.props;
    const { saving } = this.state;

    if (saving) return;

    this.setState({ saving: true });

    const params = {
      id: Number(resolve(match, 'params/user', 0)),
      role: USER_TYPE_MAP[`${e.target.value}`],
    };

    try {
      await Query(`
        mutation {
          update_user(${jsonToInputParams(params)}) {
            success
          }
        }
      `);
    } catch (e) {
      console.error(e);
    }

    reload_graph(true, true);
    this.setState({ saving: false });
  };

  handleToggleDemoUser = async (e) => {
    const { match, reload_graph, graph_data } = this.props;
    const { saving } = this.state;

    if (saving) return;

    this.setState({ saving: true });

    const params = {
      id: Number(resolve(match, 'params/user', 0)),
      demo: !resolve(graph_data, 'user/demo', false),
    };

    try {
      await Query(`
        mutation {
          update_user(${jsonToInputParams(params)}) {
            success
          }
        }
      `);
    } catch (e) {
      console.error(e);
    }

    reload_graph(true, true);
    this.setState({ saving: false });
  };

  handleResetPassword = async () => {
    const { match } = this.props;
    const userID = match.params.user;
    if (await Confirm('Are you sure?')) {
      let NotificationComponent = NotificationError;
      let message = 'There was a problem resetting the password';
      try {
        const { data } = await resetPasswordMutation(userID);
        const { success, reset_link } = resetPasswordMutationParseQuery(data);
        if (success) {
          NotificationComponent = Success;
          message = 'Password reset email was successfully sent';
          this.setState({ reset_link: reset_link, copyModalActive: true });
        }
      } catch (e) {
        console.error(e);
      }
      Notification.new(
        { timeout: 5000 },
        <NotificationComponent>{message}</NotificationComponent>
      );
    }
  };

  syncUserWithAuth = async () => {
    const { graph_data } = this.props;
    const params = jsonToInputParams({ uuid: graph_data?.user?.uuid });
    try {
      const { data } = await AdminQuery(`
        mutation {
          sync_user_to_auth(${params}) {
            success
            errors
          }
        }
      `);
      if (data?.sync_user_to_auth?.success) {
        NotificationRender(
          'success',
          'The user was successfully added to auth'
        );
        return;
      }
    } catch (e) {
      console.error(e);
    }
    NotificationRender('error', 'There was a problem adding the user to auth');
  };

  toggleProfessionalCenterSuperAdminAccess = async () => {
    const { match, graph_data } = this.props;
    const mutationName = graph_data?.user?.professional_super_admin
      ? `internal_admin_revoke_professional_superadmin_access`
      : `internal_admin_grant_professional_superadmin_access`;
    const params = jsonToInputParams({ user_id: Number(match?.params?.user) });
    try {
      const { data } = await AdminQuery(`
        mutation {
          toggle_access:${mutationName}(${params}) {
            success
            errors
          }
        }
      `);
      if (data?.toggle_access?.success) {
        this.props.reload_graph();
        NotificationRender('success');
        return;
      }
    } catch (e) {
      console.error(e);
    }
    NotificationRender(
      'error',
      'There was an error toggling professional center super admin access'
    );
  };

  render() {
    const { graph_loading, graph_data } = this.props;

    if (graph_loading) {
      return <Loading />;
    }

    return (
      <StyledUserOverview>
        <label>
          <span>Is Demo User</span>
          <Toggle
            checked={resolve(graph_data, 'user/demo', false)}
            name={'is-demo-user'}
            onChange={this.handleToggleDemoUser}
          />
        </label>
        <label>
          <span>Survey Token</span>
          <span>{resolve(graph_data, 'user/survey_token', null)}</span>
        </label>
        <label>
          <span>MCI ID</span>
          <span>{resolve(graph_data, 'user/mci_id', 'no mci id')}</span>
        </label>
        <label>
          <span>
            <span>Add user to auth</span>
            <button onClick={this.syncUserWithAuth}>Add user to auth</button>
          </span>
        </label>
        <label>
          <span>Password Reset</span>
          <ResetPassword
            toggleActive={this.toggleActive}
            active={this.state.copyModalActive}
            handleResetPassword={this.handleResetPassword}
            reset_link={this.state.reset_link}
          />
        </label>
        <label>
          <span>User Role</span>
          <select
            style={{ width: 175 }}
            value={resolve(graph_data, 'user/role', 'User')}
            onChange={this.handleUserRole}
          >
            {Object.keys(USER_TYPE_MAP).map((k) => (
              <option value={k} key={k}>
                {USER_TYPE_MAP[k]}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Professional Super Admin</span>
          <button onClick={this.toggleProfessionalCenterSuperAdminAccess}>
            {graph_data?.user?.professional_super_admin ? 'Revoke' : 'Grant'}{' '}
            Professional Super Admin Access
          </button>
        </label>
      </StyledUserOverview>
    );
  }
}

UserOverview.query = async (token, { match }) =>
  await AdminQuery(`
    {
      user (id: ${resolve(match, 'params/user', 0)}) {
        uuid,
        survey_token,
        professional_super_admin
        mci_id,
        role,
        demo
      }
    }
  `);

export default withData(UserOverview);
